import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { productionAPI } from '../api';
import { t } from '../i18n';
import toast from 'react-hot-toast';

// ─── Журнал действий ─────────────────────────────────────────────────────────
//
// Раньше на вопрос «кто это изменил» отвечали раскопками: часть следов лежала
// в истории пересортицы, часть в правках приёмки, а входы не писались вовсе —
// через JWT Django не заполняет last_login, и у всех, кроме админа, он пустой.
//
// Здесь одна лента: вход в систему и действия, из-за которых спорят — сорт
// листа, приёмка смены, отгрузка, закрытие заказа, рецепт, правка выпуска.

const COLORS = {
  login:        'var(--dim)',
  sheet_grade:  'var(--warning)',
  acceptance:   'var(--success)',
  shipment:     'var(--accent)',
  order_close:  'var(--accent)',
  order_create: 'var(--accent)',
  recipe:       'var(--warning)',
  qty_fix:      'var(--warning)',
  backfill:     'var(--danger)',
  user:         'var(--danger)',
};

const PERIODS = [
  { days: 1,  label: 'Сутки'  },
  { days: 7,  label: 'Неделя' },
  { days: 30, label: 'Месяц'  },
  { days: 0,  label: 'Всё'    },
];

const fmt = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', hour: '2-digit',
    minute: '2-digit', second: '2-digit',
  });
};

export default function AuditLog() {
  const [rows, setRows]       = useState([]);
  const [actions, setActions] = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [days, setDays]       = useState(7);
  const [act, setAct]         = useState('');
  const [q, setQ]             = useState('');
  const [query, setQuery]     = useState('');
  const [limit, setLimit]     = useState(100);

  useEffect(() => {
    const id = setTimeout(() => setQuery(q.trim()), 400);
    return () => clearTimeout(id);
  }, [q]);

  const load = useCallback(() => {
    setLoading(true);
    productionAPI.getAudit({ days, action: act || undefined, q: query || undefined, limit })
      .then(r => { setRows(r.data.results || []); setTotal(r.data.count || 0);
                   setActions(r.data.actions || []); })
      .catch(err => toast.error(err.response?.data?.error || t('Журнал не загрузился')))
      .finally(() => setLoading(false));
  }, [days, act, query, limit]);

  useEffect(() => { load(); }, [load]);

  const chip = (on) => ({
    padding: '7px 13px', borderRadius: 8, fontSize: 12.5, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
    border: '1px solid ' + (on ? 'var(--accent)' : 'var(--border)'),
    background: on ? 'var(--accent-weak)' : 'var(--card)',
    color: on ? 'var(--text)' : 'var(--muted)',
  });
  const th = {
    textAlign: 'left', padding: '9px 12px', fontSize: 11, fontWeight: 700,
    letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--dim)',
    borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
  };
  const td = {
    padding: '9px 12px', fontSize: 13, color: 'var(--text)',
    borderBottom: '1px solid var(--border)', verticalAlign: 'top',
  };

  return (
    <Layout title={t('Журнал действий')}>
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', maxWidth: 800, lineHeight: 1.5 }}>
          Кто, когда и что сделал: входы в систему и действия, меняющие товар
          или чужую работу — сорт листа, приёмка смены, отгрузка, закрытие
          заказа, рецепт, правка выпуска.
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder={t('Кто, что, номер…')}
            style={{
              flex: '1 1 240px', minWidth: 190, padding: '8px 12px', borderRadius: 8,
              border: '1px solid var(--border)', background: 'var(--input)',
              color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
            }} />
          <div style={{ display: 'flex', gap: 6 }}>
            {PERIODS.map(p => (
              <button key={p.days} onClick={() => setDays(p.days)} style={chip(days === p.days)}>
                {t(p.label)}
              </button>
            ))}
          </div>
          <select value={act} onChange={e => setAct(e.target.value)} style={{
            padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)',
            background: 'var(--input)', color: 'var(--text)', fontSize: 13,
            fontFamily: 'inherit',
          }}>
            <option value="">{t('Все действия')}</option>
            {actions.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
          </select>
          <button onClick={load} style={chip(false)}>⟳ {t('Обновить')}</button>
        </div>

        <div style={{
          background: 'var(--card)', border: '1px solid var(--border)',
          borderRadius: 12, overflow: 'hidden',
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
              <thead>
                <tr>
                  <th style={th}>{t('Когда')}</th>
                  <th style={th}>{t('Кто')}</th>
                  <th style={th}>{t('Действие')}</th>
                  <th style={th}>{t('Что сделал')}</th>
                  <th style={th}>IP</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td style={{ ...td, textAlign: 'center', color: 'var(--dim)', padding: 30 }} colSpan={5}>
                    {t('Загрузка…')}
                  </td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td style={{ ...td, textAlign: 'center', color: 'var(--dim)', padding: 30 }} colSpan={5}>
                    {t('Записей нет')}
                  </td></tr>
                )}
                {!loading && rows.map(r => (
                  <tr key={r.id}>
                    <td style={{ ...td, whiteSpace: 'nowrap', color: 'var(--muted)' }}>{fmt(r.at)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 600 }}>{r.who}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <span style={{ color: COLORS[r.action] || 'var(--muted)', fontSize: 12, fontWeight: 700 }}>
                        ● {r.action_display}
                      </span>
                    </td>
                    <td style={td}>{r.summary}</td>
                    <td style={{ ...td, color: 'var(--dim)', fontSize: 12, whiteSpace: 'nowrap' }}>{r.ip || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12, color: 'var(--dim)' }}>
            {t('Показано')} {rows.length} {t('из')} {total}
          </div>
          {rows.length < total && (
            <button onClick={() => setLimit(limit + 100)} style={chip(false)}>
              {t('Показать ещё')}
            </button>
          )}
        </div>
      </div>
    </Layout>
  );
}
